import {
  useDispatch,
  useLanguage,
  useRootStore,
  useTranslation,
  useUserStore,
} from "@/stores/RootStoreContext";
import { Icon } from "@/components-v2/Icon";
import { Box, Container, VBox } from "@/components-v2/Box";
import {
  adminShareholderDetails,
  adminShareholdersShareholderContributionLog,
  adminShareholdersShareholderContributions,
  navigateToNewShareholderContribution,
  navigateToShareholderContributionDetails,
  navigationState,
} from "@/utils/navigation";
import { FC, useEffect, useMemo } from "react";
import {
  ContributionRow,
  filterContributionRows,
  prepareContributionRows,
  prepareContributionsEmptyState,
  prepareContributionsTable,
  prepareOverviewRows,
  ShareholderContributionController,
  ShareholderContributionData,
  ShareholderContributionSubpage,
  shareholderContributionTypeLabel,
} from "./shareholderContributionsDomain";
import { isCancelled, isFailure, isPending, Result } from "@/common/common";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import { observer } from "mobx-react";
import { DataTable, TableColumn } from "@/components-v2/DataTable";
import { DropdownMenu } from "@/components-v2/Dropdown";
import { MTextInput } from "@/components-v2/TextInput";
import { formatAmountNumber } from "@/utils/currency";
import { MSelect } from "@/components-v2/Select";
import { FullScreenLayout } from "@/components-v2/FullScreenLayout";
import { useAppBar } from "@/appBar/useAppBar";
import { formatPercent } from "@/utils/numbers";
import { totalNumberOfShares } from "@/types/Shareholder";
import { Dialog } from "@/components-v2/Dialog";
import { MDateInput } from "@/components-v2/DateInput";
import { TMessage } from "@/i18n/tt";
import { LocalDateFieldModel, RadioFieldModel, StringFieldModel } from "@/common/commonForms";
import {
  ShareholderContribution,
  ShareholderContributionType,
  shareholderContributionTypes,
} from "@/shareholderRegister/shareholderRegisterDomain";
import { EmptyState } from "@/components-v2/EmptyState";
import { MenuItem } from "@/components-v2/MenuBar";
import { navigate } from "@/events/eventsDomain";

export const ShareholderContributionsPage: FC<{ activePage: ShareholderContributionSubpage }> =
  observer(function ShareholderContributionsPage({ activePage }) {
    const { t } = useTranslation();
    const language = useLanguage();
    const appBar = useAppBar();
    const { apiService, messageStore, userStore, shareholdersStore } = useRootStore();
    useEffect(() => {
      shareholdersStore.loadAllShareholders();
    }, [shareholdersStore]);

    const controller = useMemo(
      () => new ShareholderContributionController(apiService, messageStore, userStore.orgNumber),
      [apiService, messageStore, userStore.orgNumber],
    );
    useEffect(() => {
      controller.fetchShareholderContributions();
    }, [controller]);

    function render(result?: Result<ShareholderContribution[]>) {
      if (result === undefined || isPending(result)) {
        return (
          <VBox alignItems={"center"} justifyContent={"center"} fullheight>
            <LoadingIndicator />
          </VBox>
        );
      }
      if (isFailure(result) || isCancelled(result)) {
        return <LoadingError />;
      }
      if (result.data.length === 0) {
        return <EmptyState {...prepareContributionsEmptyState({ orgNo: userStore.orgNumber })} />;
      }

      return activePage === "overview" ? (
        <ShareholderContributionsOverview
          data={prepareOverviewRows(
            result.data,
            shareholdersStore.shareholders,
            totalNumberOfShares(shareholdersStore.shareSumBySharetypeCode),
          )}
        />
      ) : (
        <ShareholderContributionsTable
          data={prepareContributionRows(
            language,
            result.data,
            shareholdersStore.activeShareholders,
            t,
          )}
          textFilter={controller.textFilter}
          typeFilter={controller.typeFilter}
          controller={controller}
        />
      );
    }

    return (
      <FullScreenLayout
        appBar={appBar}
        leftPanel={<LeftPanel activePage={activePage} orgNumber={userStore.orgNumber} />}
        centerPanel={render(controller.fetchShareholderContributionsResult)}
      />
    );
  });

const LeftPanel = observer(function LeftPanel({
  orgNumber,
  activePage,
}: {
  orgNumber: string;
  activePage: ShareholderContributionSubpage;
}) {
  return (
    <VBox htmlProps={{ style: { minWidth: "300px" } }} padding={"md"}>
      <MenuItem
        icon={"users"}
        text={{ key: "overview" }}
        url={adminShareholdersShareholderContributions(orgNumber)}
        isActive={activePage === "overview"}
      />
      <MenuItem
        icon={"clock-counter-clockwise"}
        text={{ key: "shareholder-contributions.log.heading" }}
        url={adminShareholdersShareholderContributionLog(orgNumber)}
        isActive={activePage === "log"}
      />
    </VBox>
  );
});

function LoadingError() {
  const { t } = useTranslation();
  return (
    <VBox alignItems={"center"}>
      <Icon name={"warning-circle"} size={"jumbo"} color={"warning"} />
      <Box fg={"error"}>{t("shareholder-contributions.fetch-error")}</Box>
    </VBox>
  );
}

const ShareholderContributionsOverview = observer(function ShareholderContributionsOverview({
  data,
}: {
  data: ShareholderContributionData[];
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const language = useLanguage();
  const currentUrl = useRootStore().locationStr;
  const userStore = useUserStore();
  const columns: TableColumn<ShareholderContributionData>[] = [
    {
      name: "shareholder",
      label: t("shareholder-contributions.shareholder"),
      align: "left",
    },
    {
      name: "ownership",
      label: t("shareholder-contributions.ownership"),
      getValue: (row) => formatPercent(language, row.ownership),
    },
    {
      name: "committed",
      label: t("shareholder-contributions.committed"),
      getValue: (row) => formatAmountNumber(row.committed, language),
    },
    {
      name: "contributed",
      label: t("shareholder-contributions.paid-in"),
      getValue: (row) => formatAmountNumber(row.contributed, language),
    },
    {
      name: "distributed",
      label: t("shareholder-contributions.distributed"),
      getValue: (row) => formatAmountNumber(row.distributed, language),
    },
    {
      name: "recallable",
      label: t("shareholder-contributions.recallable"),
      getValue: (row) => formatAmountNumber(row.recallable, language),
    },
    {
      name: "net",
      label: t("shareholder-contributions.net"),
      getValue: (row) => formatAmountNumber(row.net, language),
    },
    {
      name: "remaining",
      label: t("shareholder-contributions.remaining"),
      getValue: (row) => formatAmountNumber(row.remaining, language),
    },
  ];

  return (
    <Container>
      <VBox gap={"md"} marginTop={"md"}>
        <Box>
          <Box grow>
            <h2>{t("shareholder-contributions.overview.heading")}</h2>
          </Box>
          <Box>
            <NewContributionButton />
          </Box>
        </Box>
        <DataTable<ShareholderContributionData>
          tableColumns={columns}
          tableData={data}
          onRowClick={(row) =>
            dispatch([
              navigate(adminShareholderDetails(userStore.orgNumber, row.id, "contributions"), {
                state: navigationState({ referrer: currentUrl }),
              }),
            ])
          }
          getId={(row) => row.id}
        />
      </VBox>
    </Container>
  );
});

const ShareholderContributionsTable = observer(function ShareholderContributionsTable({
  data,
  textFilter,
  typeFilter,
  controller,
}: {
  data: ContributionRow[];
  textFilter: StringFieldModel;
  typeFilter: RadioFieldModel<ShareholderContributionType | null>;
  controller: ShareholderContributionController;
}) {
  const { t } = useTranslation();
  const userStore = useUserStore();
  const history = useRootStore().history;

  return (
    <Container>
      <VBox gap={"md"} marginTop={"md"}>
        <Box>
          <Box grow>
            <h2>{t("shareholder-contributions.log.heading")}</h2>
          </Box>
          <Box>
            <NewContributionButton />
          </Box>
        </Box>
        <Box>
          <Box>
            <MTextInput model={textFilter} placeholder={t("search")} type={"search"} />
          </Box>
          <Box>
            <MSelect model={typeFilter} />
          </Box>
        </Box>
        <DataTable
          {...prepareContributionsTable({
            data: filterContributionRows(data, textFilter.inputValue, typeFilter.value),
            showChangeDateDialogForContribution: controller.showChangeDateDialogForContribution,
            showDeleteConfirmationDialogForContribution:
              controller.showDeleteConfirmationDialogForContribution,
            navigateToContributionDetails: (id) =>
              navigateToShareholderContributionDetails(userStore.orgNumber, id, history),
          })}
        />
      </VBox>
      <ShareholderContributionDialogs controller={controller} />
    </Container>
  );
});

export const ShareholderContributionDialogs = observer(
  ({ controller }: { controller: ShareholderContributionController }) => {
    return (
      <>
        {controller.isShowingChangeDateDialog && (
          <ChangeDateDialog
            onCancel={controller.hideChangeDateDialog}
            onSubmit={controller.submitChangeDate}
            dateModel={controller.changeDateModel}
            submitResult={controller.submitChangeDateResult}
          />
        )}
        {controller.isShowingDeleteConfirmationDialog && (
          <DeleteConfirmationDialog
            onCancel={controller.hideDeleteConfirmationDialog}
            onSubmit={controller.submitDelete}
            submitResult={controller.submitDeleteResult}
          />
        )}
      </>
    );
  },
);

function ChangeDateDialog({
  onCancel,
  onSubmit,
  dateModel,
  submitResult,
}: {
  onCancel: () => void;
  onSubmit: () => void;
  dateModel: LocalDateFieldModel;
  submitResult?: Result<TMessage, TMessage>;
}) {
  const { t } = useTranslation();
  return (
    <Dialog
      title={t("shareholder-contributions.change-date")}
      onCancel={onCancel}
      onSubmit={onSubmit}
      submitResult={submitResult}
      maxWidth={"sm"}
    >
      <VBox marginTop={"sm"}>
        <MDateInput model={dateModel} />
      </VBox>
    </Dialog>
  );
}

function DeleteConfirmationDialog({
  onCancel,
  onSubmit,
  submitResult,
}: {
  onCancel: () => void;
  onSubmit: () => void;
  submitResult?: Result<TMessage, TMessage>;
}) {
  const { t } = useTranslation();
  return (
    <Dialog
      title={t("shareholder-contributions.delete")}
      onCancel={onCancel}
      onSubmit={onSubmit}
      submitResult={submitResult}
      maxWidth={"sm"}
      submitLabel={t("shareholder-contributions.delete")}
    >
      <VBox marginTop={"sm"}>
        {submitResult === undefined && <p>{t("shareholder-contributions.delete-confirmation")}</p>}
      </VBox>
    </Dialog>
  );
}

function NewContributionButton() {
  const { t } = useTranslation();
  const userStore = useUserStore();
  const history = useRootStore().history;
  return (
    <DropdownMenu
      button={{
        type: "button",
        variant: "secondary",
        label: t("shareholder-contributions.register"),
        fullWidth: true,
      }}
      items={shareholderContributionTypes.map((type) => ({
        type: "item",
        label: t(shareholderContributionTypeLabel(type)),
        onSelect: () => navigateToNewShareholderContribution(userStore.orgNumber, history, type),
      }))}
      alignment={"end"}
    />
  );
}
